import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
// import PasswordComponent from './Components/PasswordComponents';
import CommonNodeSetting from '../otherComponent/CommonNodeSetting/CommonNodeSetting';
import { Field, Formik } from 'formik'; 
// import TextArraycomponent from './Components/TextArrayCompoent';
import StepModify from '../otherComponent/StepModify';
import { useTheme } from '@mui/material/styles';
import useApi from 'hooks/useApi';
import { IconButton } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import FieldListBuilder, { InputComponents } from './FieldListBuilder';

interface Field {
  question: string;
  description: string;
  inputComponent: InputComponents;
  options: string[];
  cameraData?: any;
  resourceType?: string;
  settings?: {
    required?: boolean;
    multiple?: boolean;
    isSupportFileRequired?: boolean;
    noOfSupportImages?: number;
    [key: string]: any;
  };
}

interface MainFormProps {
  fields: Field[];
  data?: {
    title: string;
    label?: string;
  };
  actor: any;
     onComplete: any;
   onCompleteStatusUid: string;
}
type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

interface NotificationPayload {
  conditions: string[];
  users: string[];
  roles: string[];
  channels: string[];
}

type SignFormProps = {
  selectedNodeData: any;
  selectedNode: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
};

const FormComponent = React.forwardRef(function FormComponent(
  { selectedNodeData, onSubmit, onClose, selectedNode }: SignFormProps,
  ref
) {
  const theme = useTheme();
  const { teamUid } = useParams<{ teamUid: string }>();
  const { getTeamByUid } = useApi();
  
  // State for team data
  const [teamData, setTeamData] = useState<any>(null);
  
  // Extract clientUID from node data structure, fallback to team data
  const clientUID = selectedNode?.client?.uid || selectedNode?.data?.client?.uid || teamData?.client?.uid;
  const [initialValue, setInitialValue] = useState<MainFormProps | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: [],
  });
  const [escalationPayload, setEscalationPayload] = useState({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] }
  });

// this is for finding the dependencies before deleting the field 
//  showing the warning model if there are any dependencies exist for that field



  // Expose an external save method so parent can trigger form submit (used by Sidebar Drawer)
  const externalSubmitRef = React.useRef<() => void>(() => {});
  React.useImperativeHandle(ref, () => ({
    save: () => {
      if (externalSubmitRef.current) externalSubmitRef.current();
    }
  }));


  // Load team data if clientUID is not available from node
  useEffect(() => {
    const loadTeamData = async () => {
      if (!clientUID && teamUid) {
        try {
          const response = await getTeamByUid(teamUid);
          if (response?.success && response?.data) {
            setTeamData(response.data);
            console.log('Loaded team data:', response.data);
          }
        } catch (error) {
          console.error('Failed to load team data:', error);
        }
      }
    };
    
    loadTeamData();
  }, [clientUID, teamUid, getTeamByUid]);

  // Memoized callbacks to prevent unnecessary re-renders
  const handleStatusesLoaded = useCallback((list: StatusItem[]) => {
    console.log('📋 FormComponent: Statuses loaded:', list.length);
    setStatuses(list);
  }, []);

  const handleNotificationChange = useCallback((payload: NotificationPayload) => {
    console.log('🔔 FormComponent: Notification changed:', payload);
    setNotificationPayload(payload);
  }, []);

  const handleEscalationChange = useCallback((payload: any) => {
    console.log('📞 FormComponent: Escalation changed:', payload);
    setEscalationPayload(payload);
  }, []);

  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, handleSubmit: any, shouldClearFields?: boolean) => {
    console.log('🔄 FormComponent: Step type changing to:', newType);
    setFieldValue('stepType', newType);
    setFieldValue('componentType', newType);
    setFieldValue('data.title', newType);
    if (shouldClearFields) {
      setFieldValue('fields', []); 
    }
    setTimeout(() => {
      handleSubmit();
    });
  }, []);

  // Memoize notification initial values to prevent Notification component re-renders
  const notificationInitialValues = useMemo(() => {
    // Extract notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };
  }, [selectedNode?.data?.step?.notification]);

  useEffect(() => {
    if (selectedNodeData) {
      console.log('selectedNodeData', selectedNodeData);
      const { fields = [], data = {} } = selectedNodeData;

      const title = selectedNode?.data?.label?.trim() || data?.label?.trim() || 'Untitled Checklist';
      const actor = selectedNode?.data?.step?.actor
        ?.map((a: any) => (typeof a === 'string' ? a : a?.uid))
        .filter((uid: string) => uid && uid !== 'undefined') ?? [];

      // Extract and map notification data from backend
      const backendNotification = selectedNode?.data?.step?.notification || {};
      console.log('🔍 FormComponent: Backend notification data:', backendNotification);
      
      const mappedNotification = {
        scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
        onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
        notifyRoles: backendNotification.roles || [],
        notifyUsers: backendNotification.users || [],
        notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
          channel === 'IN-APP' ? 'In-App' : 
          channel === 'EMAIL' ? 'Email' :
          channel === 'SMS' ? 'SMS' : channel
        ) : []
      };

      console.log('🔍 FormComponent: Mapped notification:', mappedNotification);

      setInitialValue({
        fields,
        data: {
          title,
          label: title
        },
        actor: actor,
        onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
        onComplete: selectedNodeData?.onComplete || {}
      });

      // Set notification payload separately to avoid type issues
      setNotificationPayload({
        conditions: [
          ...(mappedNotification.scheduleOnETA ? ['ON_ETA'] : []),
          ...(mappedNotification.onCompletion ? ['ON_COMPLETE'] : [])
        ],
        users: mappedNotification.notifyUsers || [],
        roles: mappedNotification.notifyRoles || [],
        channels: mappedNotification.notificationChannel && mappedNotification.notificationChannel.length > 0 
          ? mappedNotification.notificationChannel.map((channel: string) => 
              channel === 'In-App' ? 'IN-APP' : 
              channel === 'Email' ? 'EMAIL' :
              channel === 'SMS' ? 'SMS' : channel.toUpperCase()
            )
          : []
      });

      // Initialize escalation payload from existing node data
      const backendEscalation = selectedNode?.data?.step?.escalation || {};
      console.log('🔍 FormComponent: Backend escalation data:', backendEscalation);
      
      setEscalationPayload(selectedNode?.data?.step?.escalation || {});
      // Pass the stored object straight through. Each form used to rebuild
      // this by hand from a fixed list of keys, which silently dropped any
      // field it did not know about — including `inherit`, so opening and
      // saving any step would reset it to the process default.

      console.log('🔍 FormComponent: Set notification payload with users:', mappedNotification.notifyUsers, 'roles:', mappedNotification.notifyRoles);
    }
  }, [selectedNodeData, selectedNode]);

  return (
    initialValue && (
      <Formik
        initialValues={initialValue}
        enableReinitialize
        onSubmit={(values) => {
          const selectedStatus = statuses.find((s) => s.uid === values.onCompleteStatusUid);
          const updatedData = {
            ...values,
            data: {
              ...selectedNodeData.data,
              title: values?.data?.title
            },
            onComplete: selectedStatus ? { status: selectedStatus } : {}
          };
          const finalData = {
            ...updatedData,
            notification: notificationPayload,
            escalation: escalationPayload
          };
          onSubmit(finalData);
        }}
      >
          {({ values, setFieldValue, handleChange, handleBlur, handleSubmit }) => {
            // register Formik submit for external calls
            externalSubmitRef.current = handleSubmit;

          return (
            <Box component="form" onSubmit={handleSubmit}>
            <Box display="flex" justifyContent="center" alignItems="center"  borderBottom={1} borderColor="grey.300" p={2}>
              {isEditingTitle ? (
                <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%', maxWidth: '400px',  }}>
                  <TextField
                    name="data.title"
                    value={values.data?.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setIsEditingTitle(false);
                      }
                    }}
                    sx={{
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase' // This will capitalize each word
                    }}
                    autoFocus
                    fullWidth
                    size="small"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    
                    onClick={() => {
                      if (values.data?.title?.trim()) {
                        setIsEditingTitle(false);
                      }
                    }}
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                  <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                    <Typography 
                      variant="h5" 
                      fontWeight={500}
                      sx={{
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textTransform: 'uppercase' // This will capitalize each word
                      }}
                      title={values.data?.title || 'Untitled Form'}
                    >
                      {values.data?.title || 'Untitled Form'}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => setIsEditingTitle(true)}
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {selectedNode && (
                    <Box sx={{ minWidth: '120px', minHeight: '36px' }}>
                      <StepModify
                        selectedNode={selectedNode}
                        onStepTypeChange={(newType, shouldClearFields) => handleStepTypeChange(newType, setFieldValue, handleSubmit, shouldClearFields)}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
             

        

            <FieldListBuilder
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              selectedNodeData={selectedNodeData}
              selectedNode={selectedNode}
              onSubmit={onSubmit}
              clientUID={clientUID}
            />

            <Box mx={3} mt={-5} >
              <CommonNodeSetting
                selectedNode={selectedNode}
                onStatusesLoaded={handleStatusesLoaded}
                onNotificationChange={handleNotificationChange}
                notificationInitialValues={notificationInitialValues}
                escalationPayload={escalationPayload}
                onEscalationChange={handleEscalationChange}
              />
            </Box>

            <Box
              sx={{
                padding: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 2
              }}
            >
              <Button type="button" color="error" onClick={onClose} sx={{ '&:hover': { backgroundColor: theme.palette.error.light } }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary" sx={{ '&:hover': { backgroundColor: theme.palette.primary.dark } }}>
                save & Close
              </Button>
            </Box>

            </Box>
          );
        }}
      </Formik>
    )
  );
});

export default memo(FormComponent);
